import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, FileText, Film, LoaderCircle, RefreshCw, Plus, Calendar, ArrowRight,
  Search, Mic, X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { TranscriptModal, RecordingModal } from './VisitArtifacts';
import NewVisitModal from './NewVisitModal';

// The Video Visits tab: what's happening today (live rooms + visits tied
// to today's appointments) up top, and the clinical record of every past
// visit below — searchable, time-filtered, with transcript and recording
// one click away. Visits start here (New video visit) or from any
// appointment in Scheduling.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const durationOf = (s) => {
  if (!s.started_at || !s.ended_at) return '—';
  const mins = Math.max(1, Math.round((new Date(s.ended_at) - new Date(s.started_at)) / 60000));
  return `${mins} min`;
};
const patientOf = (s) => {
  const c = s.appointment?.client || s.client;
  const guestNames = (s.guests || []).map((g) => g.name).filter(Boolean);
  if (!guestNames.length && s.guest_name) guestNames.push(s.guest_name); // pre-050 rows
  const main = c ? [c.first_name, c.last_name].filter(Boolean).join(' ') : null;
  if (main && guestNames.length) return `${main} + ${guestNames.join(', ')}`;
  if (main) return main;
  if (guestNames.length) return `${guestNames.join(', ')} (visitor${guestNames.length > 1 ? 's' : ''})`;
  return s.appointment?.title || s.title || 'Visit';
};
const providerOf = (s) => (s.creator ? [s.creator.first_name, s.creator.last_name].filter(Boolean).join(' ') : '—');
const langChips = (s) => {
  const codes = Array.isArray(s.languages) && s.languages.length ? s.languages : ['en-US'];
  return [...new Set(codes.map((c) => String(c).slice(0, 2).toUpperCase()))];
};
const RANGES = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

// ── Test camera & mic ──────────────────────────────────────────────────────
// A pre-visit device check: live camera preview + a mic level meter, so a
// provider can confirm their setup before a patient is ever waiting.
function DeviceCheckModal({ onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [level, setLevel] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
          setLevel(Math.min(1, avg / 96));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        setErr('Could not access the camera or microphone — check browser permissions.');
      }
    })();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[440px] bg-elevated border border-theme rounded-[14px] p-5 shadow-pop"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-[14px] font-bold text-primary-c flex items-center gap-2">
            <Video size={15} className="text-accent-light" /> Test camera &amp; mic
          </span>
          <button onClick={onClose} className="w-7 h-7 rounded-md bg-tertiary text-secondary-c flex items-center justify-center hover:bg-hover-surface">
            <X size={14} />
          </button>
        </div>
        {err ? (
          <p className="text-[12.5px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5">{err}</p>
        ) : (
          <>
            <div className="rounded-[12px] overflow-hidden bg-tertiary border border-theme aspect-video mb-4">
              {/* Mirrored like every self-view, so moving left looks left. */}
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
            </div>
            <div className="flex items-center gap-2.5">
              <Mic size={14} className="text-muted-c shrink-0" />
              <div className="flex-1 h-2 rounded-full bg-tertiary overflow-hidden">
                <div
                  className="h-full rounded-full gradient-accent transition-[width] duration-100"
                  style={{ width: `${Math.round(level * 100)}%` }}
                />
              </div>
            </div>
            <p className="text-[11.5px] text-muted-c mt-3">
              Say something — if the bar moves and you see yourself, you're ready for a visit.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function TelehealthPage() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [transcriptFor, setTranscriptFor] = useState(null);
  const [recordingFor, setRecordingFor] = useState(null);
  const [newVisitOpen, setNewVisitOpen] = useState(false);
  const [deviceCheckOpen, setDeviceCheckOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('30');

  const load = useCallback(async () => {
    if (!currentOrg?.id) return;
    try {
      setSessions(await api.getTelehealthHistory(currentOrg.id));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => { load(); }, [load]);

  // Today = every joinable room: live ad-hoc visits, and open visits tied
  // to an appointment (those show their scheduled time when it's today).
  const todayScheduled = (sessions || []).filter((s) => s.status !== 'ended');

  const past = useMemo(() => {
    let rows = (sessions || []).filter((s) => s.status === 'ended');
    const r = RANGES.find((x) => x.key === range);
    if (r?.days) {
      const cutoff = Date.now() - r.days * 86400000;
      rows = rows.filter((s) => new Date(s.started_at || s.created_at).getTime() >= cutoff);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((s) =>
        patientOf(s).toLowerCase().includes(q) || providerOf(s).toLowerCase().includes(q));
    }
    return rows;
  }, [sessions, range, search]);

  const anyPastEver = (sessions || []).some((s) => s.status === 'ended');

  return (
    <div className="max-w-[1060px] mx-auto px-4 md:px-6 py-7 min-w-0">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <h1 className="text-[24px] font-bold text-primary-c">Telehealth</h1>
          <p className="text-[13px] text-muted-c mt-1 max-w-[520px]">
            Live telehealth with captions and translation. Every visit is saved here with its
            transcript and recording.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setRefreshing(true); load(); }}
            title="Refresh"
            className="w-[38px] h-[38px] rounded-[10px] text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all flex items-center justify-center"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setDeviceCheckOpen(true)}
            className="px-3.5 h-[38px] rounded-[10px] text-[12px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all flex items-center gap-1.5"
          >
            <Video size={13} /> Test camera &amp; mic
          </button>
          <button
            onClick={() => setNewVisitOpen(true)}
            className="px-4 h-[38px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 flex items-center gap-1.5"
          >
            <Plus size={14} /> New video visit
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5 mb-5">{error}</div>
      )}

      {sessions === null && !error && (
        <p className="text-[13px] text-muted-c flex items-center gap-2 py-10 justify-center">
          <LoaderCircle size={15} className="animate-spin" /> Loading visits…
        </p>
      )}

      {sessions !== null && (
        <>
          {/* Today */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[1.2px] text-muted-c">Today</h2>
            <button
              onClick={() => navigate('/app/scheduling')}
              className="text-[12.5px] font-semibold text-accent-light hover:opacity-80 transition-all flex items-center gap-1"
            >
              Open Scheduling <ArrowRight size={13} />
            </button>
          </div>
          {todayScheduled.length === 0 ? (
            <div className="flex items-center gap-4 bg-elevated rounded-[14px] border border-theme px-5 py-5 mb-8">
              <div className="w-[46px] h-[46px] rounded-[12px] bg-accent-glow text-accent-light flex items-center justify-center shrink-0">
                <Calendar size={20} />
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-bold text-primary-c">No video visits scheduled today</div>
                <div className="text-[12.5px] text-muted-c mt-0.5">
                  Add a video link to any appointment in Scheduling and it shows up here, ready to join.
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-elevated rounded-[14px] border border-theme overflow-hidden mb-8">
              {todayScheduled.map((s, i) => (
                <div key={s.id} className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? 'border-t border-theme' : ''}`}>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-primary-c truncate">{patientOf(s)}</div>
                    <div className="text-[11.5px] text-muted-c mt-0.5">
                      {s.appointment?.starts_at
                        ? `Scheduled ${fmtTime(s.appointment.starts_at)}`
                        : `Opened ${fmtTime(s.started_at || s.created_at)}`}
                      {s.recording_status === 'recording' ? ' · Recording' : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/telehealth/${s.id}`)}
                    className="px-4 py-[8px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 flex items-center gap-1.5 shrink-0"
                  >
                    <Video size={13} /> Join
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Past visits */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="text-[11px] font-bold uppercase tracking-[1.2px] text-muted-c">Past visits</h2>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              <div className="flex items-center gap-2 bg-elevated border border-theme rounded-[10px] px-3 h-[36px] flex-1 min-w-[160px] sm:flex-none sm:w-[240px]">
                <Search size={13} className="text-muted-c shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search patient or provider"
                  className="flex-1 min-w-0 bg-transparent text-[12.5px] text-primary-c placeholder:text-muted-c focus:outline-none"
                />
              </div>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="h-[36px] px-2.5 rounded-[10px] bg-elevated border border-theme text-[12.5px] font-medium text-primary-c focus:outline-none cursor-pointer"
              >
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div className="bg-elevated rounded-[14px] border border-theme overflow-hidden">
            {/* Column header */}
            <div className="hidden md:grid grid-cols-[130px_1.2fr_1fr_76px_90px_170px] gap-3 px-5 py-2.5 border-b border-theme text-[11px] font-semibold text-muted-c">
              <span>Date</span><span>Patient</span><span>Provider</span><span>Length</span><span>Language</span><span>Records</span>
            </div>

            {past.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-[52px] h-[52px] rounded-[14px] bg-accent-glow text-accent-light flex items-center justify-center mx-auto mb-3">
                  <Video size={22} />
                </div>
                <p className="text-[15px] font-bold text-primary-c">
                  {anyPastEver ? 'No visits match' : 'No video visits yet'}
                </p>
                <p className="text-[12.5px] text-muted-c mt-1 max-w-[420px] mx-auto">
                  {anyPastEver
                    ? 'Try a different search or widen the time range.'
                    : 'Finished visits land here with their transcript and recording. Start your first one now, or from any appointment in Scheduling.'}
                </p>

                {!anyPastEver && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[820px] mx-auto mt-7 text-left">
                      {[
                        ['Invite the patient', 'Send a visit link by text or email, from here or from Scheduling.'],
                        ['Talk with live captions', 'Captions and translation run during the visit for both sides.'],
                        ['Review the record', 'The transcript and recording are saved to this list.'],
                      ].map(([title, body], i) => (
                        <div key={title} className="flex items-start gap-3 bg-tertiary rounded-[12px] px-4 py-3.5">
                          <span className="w-6 h-6 rounded-full gradient-accent text-white text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <div>
                            <div className="text-[12.5px] font-bold text-primary-c">{title}</div>
                            <div className="text-[11.5px] text-muted-c mt-0.5 leading-relaxed">{body}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setNewVisitOpen(true)}
                      className="mt-7 px-5 py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5"
                    >
                      Start your first video visit
                    </button>
                  </>
                )}
              </div>
            ) : (
              past.map((s, i) => (
                <div
                  key={s.id}
                  className={`grid grid-cols-1 md:grid-cols-[130px_1.2fr_1fr_76px_90px_170px] gap-1.5 md:gap-3 px-5 py-3.5 items-center ${i > 0 ? 'border-t border-theme' : 'md:border-t-0 border-t border-theme'}`}
                >
                  <div className="text-[12px] text-secondary-c">
                    {fmtDate(s.started_at || s.created_at)}
                    <span className="text-muted-c"> · {fmtTime(s.started_at || s.created_at)}</span>
                  </div>
                  <div className="text-[13px] font-semibold text-primary-c truncate">{patientOf(s)}</div>
                  <div className="text-[12.5px] text-secondary-c truncate">{providerOf(s)}</div>
                  <div className="text-[12px] text-secondary-c">{durationOf(s)}</div>
                  <div className="flex items-center gap-1">
                    {langChips(s).map((c) => (
                      <span key={c} className="text-[10px] font-bold text-accent-light bg-accent-glow rounded-md px-1.5 py-0.5">{c}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.utterance_count > 0 && (
                      <button
                        onClick={() => setTranscriptFor(s)}
                        className="px-2.5 py-[6px] rounded-lg text-[11px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5"
                      >
                        <FileText size={11} /> Transcript
                      </button>
                    )}
                    {(s.recording_status === 'ready' || s.recording_status === 'processing') && (
                      <button
                        onClick={() => setRecordingFor(s)}
                        className="px-2.5 py-[6px] rounded-lg text-[11px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5"
                      >
                        <Film size={11} /> {s.recording_status === 'processing' ? 'Processing…' : 'Recording'}
                      </button>
                    )}
                    {s.utterance_count === 0 && !['ready', 'processing'].includes(s.recording_status) && (
                      <span className="text-[11px] text-muted-c">—</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {transcriptFor && <TranscriptModal session={transcriptFor} onClose={() => setTranscriptFor(null)} />}
      {recordingFor && <RecordingModal session={recordingFor} onClose={() => setRecordingFor(null)} />}
      {newVisitOpen && <NewVisitModal orgId={currentOrg?.id} onClose={() => setNewVisitOpen(false)} />}
      {deviceCheckOpen && <DeviceCheckModal onClose={() => setDeviceCheckOpen(false)} />}
    </div>
  );
}
